<template>
  <div class="gallery-container">
    <!-- 使用纯CSS实现的三列交错滚动画廊 -->
    <div class="marquee-container">
      <!-- 左侧列 - 向下滚动 -->
      <div class="column column-left">
        <div class="scroll-content">
          <div 
            v-for="(item, index) in leftColumn" 
            :key="'left-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
          <!-- 复制一份内容确保无缝滚动 -->
          <div 
            v-for="(item, index) in leftColumn" 
            :key="'left-clone-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 中间列 - 向上滚动 -->
      <div class="column column-middle">
        <div class="scroll-content">
          <div 
            v-for="(item, index) in middleColumn" 
            :key="'middle-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
          <!-- 复制一份内容确保无缝滚动 -->
          <div 
            v-for="(item, index) in middleColumn" 
            :key="'middle-clone-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 右侧列 - 向下滚动但速度更慢 -->
      <div class="column column-right">
        <div class="scroll-content">
          <div 
            v-for="(item, index) in rightColumn" 
            :key="'right-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
          <!-- 复制一份内容确保无缝滚动 -->
          <div 
            v-for="(item, index) in rightColumn" 
            :key="'right-clone-' + index" 
            class="gallery-item"
            @click="openLink(item.link)"
          >
            <div class="image-container">
              <img :src="item.imageName" :alt="item.title" @error="handleImageError" />
              <div class="image-overlay">
                <div class="image-title">{{ item.title }}</div>
                <div class="image-description" v-if="item.description">{{ item.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 灯箱查看大图 -->
    <div class="lightbox" v-if="lightboxVisible" @click="closeLightbox">
      <span class="close-btn">&times;</span>
      <div class="lightbox-content">
        <img :src="currentItem.imageName" :alt="currentItem.title" @error="handleImageError" />
        <div class="caption">
          {{ currentItem.title }}
          <a :href="currentItem.link" target="_blank" class="lightbox-link">访问链接</a>
          <div v-if="currentItem.description" class="description">{{ currentItem.description }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { galleryItems, GalleryItem } from '@/config/galleryConfig';

export default defineComponent({
  name: 'ImageGallery',
  setup() {
    // 使用配置文件中定义的画廊项目
    const items = ref<GalleryItem[]>(galleryItems);
    
    // 将图片分成三列
    const leftColumn = computed(() => {
      return items.value.filter((_, index) => index % 3 === 0);
    });
    
    const middleColumn = computed(() => {
      return items.value.filter((_, index) => index % 3 === 1);
    });
    
    const rightColumn = computed(() => {
      return items.value.filter((_, index) => index % 3 === 2);
    });
    
    const lightboxVisible = ref(false);
    const currentItem = ref<GalleryItem>(items.value[0]);
    
    // 错误处理 - 当图片加载失败时
    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      // 设置默认备用图片
      target.src = '/images/fallback.jpg';
      console.warn('Image failed to load:', target.alt);
    };
    
    const showLightbox = (item: GalleryItem) => {
      currentItem.value = item;
      lightboxVisible.value = true;
    };
    
    const closeLightbox = () => {
      lightboxVisible.value = false;
    };
    
    const openLink = (link: string) => {
      window.open(link, '_blank');
    };
    
    return {
      leftColumn,
      middleColumn,
      rightColumn,
      lightboxVisible,
      currentItem,
      handleImageError,
      showLightbox,
      closeLightbox,
      openLink
    };
  }
});
</script>

<style scoped>
.gallery-container {
  height: 100vh;
  overflow: hidden;
  background-color: #121212;
  color: #e0e0e0;
  padding: 5px; /* 统一边缘间距为5px */
}

.marquee-container {
  display: flex;
  height: 100%;
  gap: 5px; /* 列间距统一为5px */
}

.column {
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding: 0; /* 重置基础padding */
  box-sizing: border-box;
}

/* 单独设置列样式，避免重叠 */
.column-left, .column-middle, .column-right {
  padding: 5px;
}

.scroll-content {
  position: absolute;
  width: 100%; /* 恢复100%宽度 */
  top: 0; /* 确保滚动内容从顶部开始 */
  left: 0; /* 确保滚动内容从左侧开始 */
}

/* 恢复滚动动画 */
.column-left .scroll-content {
  animation: scrollDown 60s linear infinite;
}

.column-middle .scroll-content {
  animation: scrollUp 70s linear infinite;
}

.column-right .scroll-content {
  animation: scrollDown 80s linear infinite;
}

@keyframes scrollDown {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

@keyframes scrollUp {
  0% {
    transform: translateY(-50%);
  }
  100% {
    transform: translateY(0);
  }
}

.gallery-item {
  cursor: pointer;
  margin-bottom: 5px; /* 从15px改为5px以统一垂直间距 */
  transition: all 0.3s ease;
}

/* 悬停效果微调，确保不会因为间距小而导致视觉干扰 */
.gallery-item:hover {
  transform: scale(1.02); /* 稍微减小放大效果，避免相邻图片重叠 */
  z-index: 2; /* 确保悬停图片在上层 */
}

/* 悬停时暂停动画 */
.column:hover .scroll-content {
  animation-play-state: paused;
}

.image-container {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4); /* 减小阴影 */
  transition: all 0.3s ease;
  margin-right: 0; /* 移除右侧空间 */
}

.image-container img {
  width: 100%;
  display: block;
  aspect-ratio: 1/1;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.5s ease;
}

.gallery-item:hover img {
  transform: scale(1.08);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75); /* 更深的遮罩 */
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.4s ease;
  padding: 15px; /* 减小内边距保持内容清晰度 */
  text-align: center;
  border-radius: 16px; /* 匹配容器的圆角 */
}

.gallery-item:hover .image-overlay {
  opacity: 1;
}

.image-title {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

.image-description {
  font-size: 1rem;
  color: #e0e0e0;
}

/* 灯箱样式 */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.lightbox-content {
  position: relative;
  max-width: 85%;
  max-height: 85%;
  text-align: center;
  border-radius: 16px;
  overflow: hidden;
}

.lightbox img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 12px;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 40px;
  color: white;
  cursor: pointer;
  transition: color 0.3s ease;
}

.close-btn:hover {
  color: #ff6b6b;
}

.caption {
  color: white;
  margin-top: 15px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 20px;
  border-radius: 30px;
  display: inline-block;
}

.lightbox-link {
  display: inline-block;
  margin-left: 12px;
  color: #66d9ff;
  text-decoration: none;
  transition: color 0.3s;
  font-weight: bold;
}

.lightbox-link:hover {
  color: #1ad1ff;
  text-decoration: underline;
}

.description {
  font-size: 0.95rem;
  color: #cccccc;
  margin-top: 8px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .gallery-container {
    padding: 5px; /* 保持统一的间距 */
  }
  
  .marquee-container {
    flex-direction: column;
    gap: 5px; /* 保持统一的间距 */
  }
  
  .column {
    height: 33.33%; /* 三等分高度 */
    padding: 5px; /* 保持统一的内边距 */
  }
  
  .column-left,
  .column-middle,
  .column-right {
    padding: 5px; /* 确保响应式下也保持统一内边距 */
  }

  .gallery-item {
    margin-bottom: 5px; /* 确保移动端也保持同样的间距 */
  }
}
</style>
